// src/pages/portal/PortalMandateDetail.js — mandate detail. Renders the
// Teaser tier (opportunity overview, capital need, why we're excited, team)
// for any LIVE mandate, or the Full-detail tier (+ timing) plus
// Pipeline/Meetings/Documents if engaged — see the mandate view taxonomy in
// src/controllers/portalController.js. The API decides which tier to
// return; this component just renders what it gets.
import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import api from '../../lib/api'
import WorkTypeTags from '../../components/WorkTypeTags'
import MandateTaxonomyTags from '../../components/MandateTaxonomyTags'
import { PIPELINE_STAGES, PL_BADGE } from '../../lib/pipelineStages'
import MaterialsSection from '../../components/MaterialsSection'
import { PORTAL_MANDATES_TITLE } from '../../lib/portalPageTitles'
import { GP_WEBSITES } from '../../lib/gpWebsites'
import MandateHeader from '../../components/portal/MandateHeader'
import ExternalLinkIcon from '../../components/portal/ExternalLinkIcon'
import AddNamesButton from '../../components/portal/AddNamesButton'
import { PortalPeekLink, usePortalDirectory } from '../../components/portal/PortalPeek'

function formatCurrency(n) {
  if (n == null) return '—'
  return `$${Number(n).toLocaleString()}`
}

// Section headings use the .overview-heading class (index.css) — a
// bold treatment scaled down from MandateHeader's big mandate
// name, rather than the old small muted-gray label.
function OverviewItem({ heading, value }) {
  return (
    <div>
      <div className="overview-heading">{heading}</div>
      <div className="overview-body" style={{ color: value ? 'var(--text-primary)' : 'var(--text-muted)', fontStyle: value ? 'normal' : 'italic' }}>
        {value || 'Details coming soon.'}
      </div>
    </div>
  )
}

// Partners never see engagement terms unless RGA shares them (Matt, 10/8).
// One click records the ask (migrations/067) and alerts RGA on the
// dashboard. Once RGA shares reviewed economics with this partner, they show
// here in place of the button, as label / value rows.
function EconomicsBlock({ mandateId, requestedAt, shared }) {
  const qc = useQueryClient()
  const mutation = useMutation({
    mutationFn: () => api.post(`/api/portal/mandates/${mandateId}/economics-request`).then(r => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portalMandateDetail', mandateId] }),
  })
  const sentAt = requestedAt || mutation.data?.created_at

  if (shared && !sentAt) {
    return (
      <div>
        <div className="overview-heading">Economics</div>
        {shared.terms?.length ? (
          // Reviewed rows RGA shared with this partner (migrations/083).
          <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 14px', alignItems: 'baseline', marginTop: 4 }}>
            {shared.terms.map(t => [
              <div key={`${t.label}-l`} style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                letterSpacing: '0.06em', color: 'var(--text-muted)' }}>{t.label}</div>,
              <div key={`${t.label}-v`} className="overview-body" style={{ margin: 0 }}>{t.value}</div>,
            ])}
          </div>
        ) : (
          <div className="overview-body" style={{ whiteSpace: 'pre-wrap' }}>{shared.text}</div>
        )}
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
          Shared by RGA {new Date(shared.shared_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="overview-heading">Economics</div>
      {sentAt ? (
        <div className="overview-body" style={{ color: 'var(--text-secondary)' }}>
          Requested {new Date(sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}. RGA will be in touch.
        </div>
      ) : (
        <>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? 'Sending…' : 'Request the economics'}
          </button>
          {mutation.isError && (
            <div style={{ fontSize: 12, color: 'var(--danger-text)', marginTop: 6 }}>
              {mutation.error.response?.data?.error || 'Could not send the request. Try again.'}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function formatDateTime(d) {
  if (!d) return '—'
  return new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function attendeesOf(meeting) {
  const all = [meeting.sender_email, ...(meeting.to_emails || []), ...(meeting.cc_emails || [])]
  return [...new Set(all.filter(Boolean))]
}

function MeetingRow({ meeting }) {
  return (
    <div style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 2 }}>
        {meeting.subject_title || 'Untitled meeting'}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
        {formatDateTime(meeting.date)}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        {attendeesOf(meeting).join(', ')}
      </div>
    </div>
  )
}

function MeetingsSection({ mandateId }) {
  const { data, isLoading } = useQuery({
    queryKey: ['portalMeetings', mandateId],
    queryFn: () => api.get(`/api/portal/mandates/${mandateId}/meetings`).then(r => r.data.data),
  })

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div className="spinner spinner-lg" />
      </div>
    )
  }

  const meetings = data || []
  const now = Date.now()
  const upcoming  = meetings.filter(m => m.date && new Date(m.date).getTime() >= now)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
  const completed = meetings.filter(m => !m.date || new Date(m.date).getTime() < now)
    .sort((a, b) => new Date(b.date) - new Date(a.date))

  if (!meetings.length) {
    return (
      <div className="empty-state">
        <h3>No meetings yet</h3>
        <p>Meetings tied to this mandate will show up here.</p>
      </div>
    )
  }

  return (
    <div className="card" style={{ padding: 20 }}>
      {upcoming.length > 0 && (
        <div style={{ marginBottom: completed.length ? 20 : 0 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>
            Upcoming
          </div>
          {upcoming.map(m => <MeetingRow key={m.interaction_id} meeting={m} />)}
        </div>
      )}
      {completed.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>
            Completed
          </div>
          {completed.map(m => <MeetingRow key={m.interaction_id} meeting={m} />)}
        </div>
      )}
    </div>
  )
}

const shortDate = d => (d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—')

// One investor in Your Pipeline. Stage, the date it was reached, and the
// partner's own notes are edited in place; the row opens to its stage
// history (migrations/092).
function PipelineRow({ row, ids, onSave }) {
  const [open, setOpen] = useState(false)
  const [notes, setNotes] = useState(row.partner_notes ?? '')
  useEffect(() => { setNotes(row.partner_notes ?? '') }, [row.partner_notes])
  const ref = ids.get(row.pipeline_id)
  return (
    <>
      <tr>
        <td>
          <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} title="Stage history"
            style={{ background: 'none', border: 'none', padding: '0 6px 0 0', cursor: 'pointer', color: 'var(--text-muted)' }}>
            {open ? '▾' : '▸'}
          </button>
          {row.investor_contact_name
            ? <PortalPeekLink type="contact" id={ref?.contact_id}>{row.investor_contact_name}</PortalPeekLink>
            : '—'}
        </td>
        <td>
          {row.investor_firm_name
            ? <PortalPeekLink type="firm" id={ref?.investor_firm_id}>{row.investor_firm_name}</PortalPeekLink>
            : '—'}
        </td>
        <td>
          <select className="select" value={row.stage} aria-label="Stage"
            onChange={e => onSave(row.pipeline_id, { stage: e.target.value, stage_date: new Date().toLocaleDateString('en-CA') })}
            style={{ fontSize: 12, padding: '4px 8px', maxWidth: 210 }}>
            {PIPELINE_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </td>
        <td>
          <input type="date" className="input" value={row.stage_date ?? ''} aria-label="Date reached"
            onChange={e => onSave(row.pipeline_id, { stage_date: e.target.value || null })}
            style={{ fontSize: 12, padding: '4px 6px', maxWidth: 150 }} />
        </td>
        <td style={{ minWidth: 200 }}>
          <input className="input" value={notes} placeholder="Add a note" aria-label="Your notes"
            onChange={e => setNotes(e.target.value)}
            onBlur={() => { if ((notes.trim() || null) !== (row.partner_notes ?? null)) onSave(row.pipeline_id, { partner_notes: notes }) }}
            style={{ fontSize: 12, padding: '4px 8px' }} />
        </td>
        <td>{formatCurrency(row.target_commitment)}</td>
        <td>{formatCurrency(row.actual_commitment)}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} style={{ background: 'var(--surface-raised)', fontSize: 12 }}>
            {!row.history?.length ? <span style={{ color: 'var(--text-muted)' }}>No stage history yet.</span> : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px' }}>
                {row.history.map(h => (
                  <span key={h.history_id}>
                    <span className={`badge ${PL_BADGE[h.stage] ?? 'badge-gray'}`} style={{ fontSize: 10, marginRight: 6 }}>{h.stage}</span>
                    {h.occurred_on ? shortDate(h.occurred_on) : <span style={{ color: 'var(--text-muted)' }}>date not set</span>}
                  </span>
                ))}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

function PipelineTable({ mandateId }) {
  // The directory carries each row's contact and firm ids, so names open
  // their portal pages (Travis, 11/3).
  const { data: directory } = usePortalDirectory()
  const ids = new Map((directory ?? []).map(r => [r.pipeline_id, r]))
  const qc = useQueryClient()
  const key = ['portalPipeline', mandateId]
  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.get(`/api/portal/mandates/${mandateId}/pipeline`).then(r => r.data.data),
  })
  const [stageFilter, setStageFilter] = useState('')
  const [search, setSearch] = useState('')

  // The partner works their own investors (Travis and Matt, 11/3): stage,
  // date reached, notes. Optimistic, rolled back on error.
  const [saveError, setSaveError] = useState(null)
  const save = useMutation({
    mutationFn: ({ pipelineId, patch }) => api.patch(`/api/portal/pipeline/${pipelineId}`, patch).then(r => r.data.data),
    onMutate: async ({ pipelineId, patch }) => {
      setSaveError(null)
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData(key)
      qc.setQueryData(key, rows => (rows ?? []).map(r => r.pipeline_id === pipelineId ? { ...r, ...patch } : r))
      return { prev }
    },
    onSuccess: fresh => qc.setQueryData(key, rows => (rows ?? []).map(r => r.pipeline_id === fresh.pipeline_id ? { ...r, ...fresh } : r)),
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev)
      setSaveError(e.response?.data?.error || e.message)
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['portalInvestors'] })
      qc.invalidateQueries({ queryKey: ['portalDashboard'] })
    },
  })

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div className="spinner spinner-lg" />
      </div>
    )
  }

  if (!data?.length) {
    return (
      <div className="empty-state">
        <h3>No attribution yet</h3>
        <p>You have no investors attributed to you on this mandate.</p>
      </div>
    )
  }

  const counts = data.reduce((m, r) => ({ ...m, [r.stage]: (m[r.stage] ?? 0) + 1 }), {})
  const q = search.trim().toLowerCase()
  const rows = data
    .filter(r => !stageFilter || r.stage === stageFilter)
    .filter(r => !q || [r.investor_contact_name, r.investor_firm_name, r.partner_notes].some(v => (v || '').toLowerCase().includes(q)))

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 12 }}>
        <button type="button" className={`btn btn-sm ${!stageFilter ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setStageFilter('')}>
          All {data.length}
        </button>
        {PIPELINE_STAGES.filter(s => counts[s]).map(s => (
          <button key={s} type="button" className={`btn btn-sm ${stageFilter === s ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setStageFilter(f => f === s ? '' : s)}>
            {s} {counts[s]}
          </button>
        ))}
        <input className="input" placeholder="Search investors or notes" value={search} onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 240, marginLeft: 'auto', fontSize: 12 }} />
      </div>
      {saveError && <div style={{ fontSize: 12, color: 'var(--danger-text)', marginBottom: 8 }}>{saveError}</div>}
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Investor</th>
              <th>Firm</th>
              <th>Stage</th>
              <th>Date reached</th>
              <th>Your notes</th>
              <th>Target</th>
              <th>Committed</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <PipelineRow key={row.pipeline_id} row={row} ids={ids}
                onSave={(pipelineId, patch) => save.mutate({ pipelineId, patch })} />
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

// Key Metrics (Matt, 11/3): every filled metric, for every partner who can
// see the mandate. A fund's track record shows as firm / fund pairs (IRR,
// MOIC, DPI), firm first; everything else one row each.
function KeyMetricsBox({ mandateId }) {
  const { data } = useQuery({
    queryKey: ['portalKeyMetrics', mandateId],
    queryFn: () => api.get(`/api/portal/mandates/${mandateId}/key-metrics`).then(r => r.data.data),
  })
  const metrics = data?.metrics ?? []
  if (!metrics.length) return null
  const rows = []
  for (const m of metrics) {
    if (!m.pair) { rows.push({ label: m.label, lines: [{ value: m.value }] }); continue }
    const row = rows.find(r => r.pair === m.pair)
    if (row) row.lines.push({ side: m.side, value: m.value })
    else rows.push({ pair: m.pair, label: m.pair, lines: [{ side: m.side, value: m.value }] })
  }
  return (
    <div className="portal-tiles">
      <div className="portal-tile span-4">
        <div className="overview-heading">Key Metrics</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '10px 22px', fontSize: 15, marginTop: 6 }}>
          {rows.map(r => (
            <div key={r.label} style={{ display: 'contents' }}>
              <div style={{ color: 'var(--text-muted)', fontSize: 13, paddingTop: 1 }}>{r.label}</div>
              <div>
                {r.lines.map((l, i) => (
                  <div key={i} style={{ whiteSpace: 'pre-line' }}>
                    {l.side && <span style={{ color: 'var(--text-muted)', fontSize: 13, display: 'inline-block', minWidth: 44 }}>{l.side}</span>}
                    {l.value}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default function PortalMandateDetail() {
  const { id } = useParams()

  const { data: mandate, isLoading, error } = useQuery({
    queryKey: ['portalMandateDetail', id],
    queryFn: () => api.get(`/api/portal/mandates/${id}`).then(r => r.data.data),
  })

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <div className="spinner spinner-lg" />
      </div>
    )
  }

  if (error || !mandate) {
    return (
      <div className="page">
        <div className="empty-state">
          <h3>Mandate not found</h3>
          <p><Link to="/portal/mandates">Back to {PORTAL_MANDATES_TITLE}</Link></p>
        </div>
      </div>
    )
  }

  const website = GP_WEBSITES[mandate.mandate_name]

  return (
    <div className="page">
      <MandateHeader mandate={mandate} back={{ to: '/portal/mandates', label: PORTAL_MANDATES_TITLE }} />

      {/* Engaged mandates only for now (open with Emily, 10/14). */}
      <WorkTypeTags workType={mandate.work_type} workSubtype={mandate.work_subtype} style={{ marginBottom: 10 }} />
      <MandateTaxonomyTags tags={mandate.taxonomy_tags} style={{ marginBottom: 16 }} />
      {mandate.bd_targets && (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
          Business development targets: <span style={{ color: 'var(--text-primary)' }}>{mandate.bd_targets}</span>
        </div>
      )}

      {/* Quick facts first, each in its own box; then the narrative, one box per section. */}
      <div className="portal-tiles">
        <div className="portal-tile"><OverviewItem heading="Capital Need" value={mandate.capital_need} /></div>
        <div className="portal-tile"><OverviewItem heading="Target Close" value={mandate.target_close || 'TBD'} /></div>
        {mandate.engaged && (
          <div className="portal-tile"><OverviewItem heading="Timing" value={mandate.timing} /></div>
        )}
        <div className={`portal-tile${mandate.engaged ? '' : ' span-2'}`}>
          <EconomicsBlock mandateId={mandate.mandate_id}
            requestedAt={mandate.economics_requested_at} shared={mandate.economics_shared} />
        </div>
        <div className="portal-tile span-2" style={{ position: 'relative' }}>
          <OverviewItem heading="Opportunity Overview" value={mandate.opportunity_overview} />
          {website && (
            <a
              href={website}
              target="_blank"
              rel="noreferrer noopener"
              title={mandate.gp_firm_name ? `Visit ${mandate.gp_firm_name}` : 'Visit website'}
              style={{
                position: 'absolute', top: 14, right: 14,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: 6,
                border: '1px solid var(--border)', color: 'var(--text-secondary)',
              }}
            >
              <ExternalLinkIcon />
            </a>
          )}
        </div>
        {/* Team beside the overview; Why We're Excited full width below (Travis, 11/3). */}
        {mandate.team && (
          <div className="portal-tile span-2"><OverviewItem heading="Team" value={mandate.team} /></div>
        )}
        {mandate.why_excited && (
          <div className={`portal-tile ${mandate.team ? 'span-4' : 'span-2'}`}><OverviewItem heading="Why We're Excited" value={mandate.why_excited} /></div>
        )}
      </div>

      <KeyMetricsBox mandateId={mandate.mandate_id} />

      {!mandate.engaged && (
        <div className="empty-state" style={{ marginBottom: 20 }}>
          <Link to={`/portal/mandates/${mandate.mandate_id}/interest`} className="btn btn-primary btn-sm">
            Interested in engaging?
          </Link>
        </div>
      )}

      {mandate.engaged && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>Your Pipeline</h2>
            <AddNamesButton to={`/portal/mandates/${mandate.mandate_id}/add-names`} className="btn btn-primary btn-sm" />
          </div>
          <PipelineTable mandateId={mandate.mandate_id} />
        </div>
      )}

      {mandate.engaged && (
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: 'var(--text-primary)' }}>Meetings</h2>
          <MeetingsSection mandateId={mandate.mandate_id} />
        </div>
      )}

      {/* Materials RGA chose for this mandate's work type (migrations/079).
          Replaces the old Documents list: partners see only these. */}
      {mandate.engaged && (
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, color: 'var(--text-primary)' }}>Materials</h2>
          <MaterialsSection mandateId={mandate.mandate_id} mode="portal" />
        </div>
      )}
    </div>
  )
}
